/**
 * Table / TableHeader / TableRow / TableCell / TableIndexCell — the DS list table
 * (PACK-TYPE + MONITORING-LIST 2026-08-20, spec P8 / decision D9).
 *
 * PORT, NOT COPY. The skill's components/data-display/{Table,TableHeader}.jsx are
 * inline-style with raw px (`padding '10px 12px'`, `fontSize 14`, `padding '12px'`);
 * the shipped DS is class-based, so every visual property lands here as an `oc-table*`
 * class over ds.css tokens (D9, the idiom of ds.css:1-16). Only COLUMN WIDTHS stay
 * inline — they are per-call-site data no class can carry, and `width` is exactly what
 * the CSSPX gate carves out. The row hover tint likewise stops being React state and
 * becomes `:hover`, which is what a class layer is for.
 *
 * API KEPT — names, semantics and defaults (`numbered = true`, `startIndex = 1`,
 * `minWidth`), and with them the sizing rule that IS the design system's §4.6 rules 1–2:
 * a cell WITH `width` is fixed (no grow, no shrink); the one cell WITHOUT `width` takes
 * the remaining space; header and body repeat the same value.
 *
 * ONE ADDITIVE FLAG — `TableRow detail` (D9, round-1 finding C-1/J-1). A detail row is
 * the expansion of the data row above it: the numbering walk skips it exactly as it
 * already skips `header`, so it carries NO «№» cell and does NOT advance the counter —
 * expanding or collapsing a row leaves every other row's number unchanged (AC-10). Its
 * children render inside a single flex-1 cell with no `width`, i.e. genuinely
 * full-width. `detail` is destructured here and never reaches the DOM `div` through the
 * `...rest` spread (round-2 finding J2-2), like `header` and `selected` before it.
 */
import { Children, cloneElement, isValidElement } from 'react';
import { Icon } from './Icon.jsx';

const INDEX_WIDTH = 44;

function colStyle (width, style) {
  return width == null ? style : { flexBasis: width, width, ...style };
}

function alignClass (align) {
  if (align === 'right') return 'oc-table__cell--right';
  if (align === 'center') return 'oc-table__cell--center';
  return null;
}

/** TableCell — body cell: Inter 14 regular, ellipsis overflow, `muted` = secondary text. */
export function TableCell ({ children, align = 'left', width, muted = false, className, style, ...rest }) {
  return (
    <div
      className={[
        'oc-table__cell',
        width == null ? null : 'oc-table__cell--fixed',
        alignClass(align),
        muted ? 'oc-table__cell--muted' : null,
        className,
      ].filter(Boolean).join(' ')}
      style={colStyle(width, style)}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * TableIndexCell — the «№» column. Every list of bloggers/leads is numbered, so this is
 * the first cell of every data row: fixed 44, tertiary colour, tabular figures,
 * right-aligned so 1 and 100 share a decimal edge.
 */
export function TableIndexCell ({ n, width = INDEX_WIDTH, className, ...rest }) {
  return (
    <TableCell
      width={width}
      align="right"
      className={['oc-table__cell--index', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {n}
    </TableCell>
  );
}

/**
 * TableHeader — a column header cell: uppercase Inter 12 medium, tertiary, 0.05em
 * tracking, horizontally flush with the body cells. `sortable` shows the double-caret
 * affordance. Same sizing rule as TableCell — mirror the body cell's `width`.
 */
export function TableHeader ({ children, sortable = false, sorted, align = 'left', width, className, style, ...rest }) {
  return (
    <div
      className={[
        'oc-table__cell',
        'oc-table__cell--header',
        width == null ? null : 'oc-table__cell--fixed',
        alignClass(align),
        sortable ? 'oc-table__cell--sortable' : null,
        className,
      ].filter(Boolean).join(' ')}
      style={colStyle(width, style)}
      {...rest}
    >
      {children}
      {sortable && (
        <Icon
          name={sorted === 'desc' ? 'caret-down' : sorted === 'asc' ? 'caret-up' : 'caret-up-down'}
          size="sm"
          color="tertiary"
        />
      )}
    </div>
  );
}

/**
 * TableRow — flex row with a hairline divider; hover tints, header and detail rows do
 * not. `detail` wraps the children in the single full-width cell described at the top.
 */
export function TableRow ({ children, header = false, selected = false, detail = false, className, ...rest }) {
  return (
    <div
      className={[
        'oc-table__row',
        header ? 'oc-table__row--header' : null,
        detail ? 'oc-table__row--detail' : null,
        selected ? 'oc-table__row--selected' : null,
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {detail ? <div className="oc-table__cell oc-table__cell--detail">{children}</div> : children}
    </div>
  );
}

/**
 * Table — white surface, hairline outline, radius 8.
 * `numbered` (ON by default) prepends the «№» column to every row and the matching
 * header cell, so a list is never unnumbered by accident; `startIndex` is what carries
 * the numbering across client-paginated pages. `minWidth` keeps fixed columns at their
 * real width and scrolls horizontally instead of squeezing them.
 */
export function Table ({ children, numbered = true, startIndex = 1, minWidth, className, style, ...rest }) {
  let n = startIndex;
  const rows = !numbered ? children : Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    // A detail row is the row above it, expanded: no «№», no counter advance.
    if (child.props?.detail) return child;
    const extra = child.props?.header
      ? <TableHeader key="__num" width={INDEX_WIDTH} align="right">№</TableHeader>
      : <TableIndexCell key="__num" n={n++} />;
    return cloneElement(child, undefined, [extra, ...Children.toArray(child.props.children)]);
  });

  return (
    <div className={['oc-table', className].filter(Boolean).join(' ')} style={style} {...rest}>
      <div className={`oc-table__scroll${minWidth == null ? ' oc-table__scroll--static' : ''}`}>
        <div className="oc-table__grid" style={minWidth == null ? undefined : { minWidth }}>
          {rows}
        </div>
      </div>
    </div>
  );
}
